import { useEffect, useState } from 'react';
import type { Module } from '@pc/core';
import { SchemaParityError } from '@pc/db';

/**
 * Dev-mode startup parity banner.
 *
 * Walks the module registry and runs each module's `healthProbe()`. If any
 * probe reports the schema isn't reachable through PostgREST (PGRST106 /
 * "Invalid schema"), we render a hard, persistent banner BEFORE the user
 * can click anything. This converts the next instance of the
 * "module-registered-but-DB-not-applied" bug from a cryptic mid-flow error
 * into a clear, actionable signal at boot time.
 *
 * No-op in production builds.
 */
interface ParityFailure {
  moduleId: string;
  moduleName: string;
  schema: string;
  view: string;
  message: string;
  kind: string;
}

export function DevSchemaParityBanner({ modules }: { modules: ReadonlyArray<Module> }) {
  const [failures, setFailures] = useState<ParityFailure[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    const next: ParityFailure[] = [];
    Promise.all(
      modules.map(async (m) => {
        if (!m.healthProbe) return;
        try {
          await m.healthProbe();
        } catch (err) {
          if (err instanceof SchemaParityError) {
            next.push({
              moduleId: m.id,
              moduleName: m.name,
              schema: err.failure.schema,
              view: err.failure.view,
              message: err.failure.message,
              kind: err.failure.kind,
            });
          } else {
            next.push({
              moduleId: m.id,
              moduleName: m.name,
              schema: m.id,
              view: '?',
              message: err instanceof Error ? err.message : String(err),
              kind: 'unknown',
            });
          }
        }
      }),
    ).then(() => {
      if (cancelled) return;
      setFailures(next);
    });
    return () => {
      cancelled = true;
    };
  }, [modules]);

  if (!import.meta.env.DEV) return null;
  if (failures.length === 0 || dismissed) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-50 border-b border-rule-strong bg-[#A8451E] text-paper">
      <div className="mx-auto max-w-[1200px] px-6 py-3 text-sm">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-medium uppercase tracking-wider text-xs mb-1">
              Schema parity failure ({failures.length} module
              {failures.length === 1 ? '' : 's'})
            </p>
            <ul className="space-y-2">
              {failures.map((f) => (
                <li key={f.moduleId}>
                  <p className="font-medium">
                    {f.moduleName} ({f.schema}.{f.view}) - {f.kind}
                  </p>
                  <p className="text-paper/80 text-xs leading-relaxed">{f.message}</p>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-paper/70">
              This banner only renders in dev. Apply the missing migration with
              the Supabase MCP <code>apply_migration</code> tool, then refresh.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-xs uppercase tracking-wider underline shrink-0"
          >
            dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
